import classNames from 'classnames';
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ReactComponent as PlusIcon } from 'assets/images/icons/plus.svg';
import { ReactComponent as TrashIcon } from 'assets/images/icons/trash.svg';

import { Button, Input, Select } from 'ui';
import Card from 'ui/Card';

import { IUserCreateZones } from 'entities/view/userCreate';
import useDebounce from 'hooks/useDebounce';
import { PlantFamily, plantsApi, PlantSearchResult } from 'services/plants';
import { SOIL_TYPE } from 'utils/constants';

type PropTypes = {
  zone: IUserCreateZones;
  zoneIndex: number;
  detailIndex: number;
  hasSensor?: boolean;
  onAddZoneSensor: (detailIndex: number, zoneIndex: number) => void;
  onRemoveZone?: (detailIndex: number, zoneIndex: number) => void;
  onFieldChange: (detailIndex: number, zoneIndex: number, field: string, value: string) => void;
  onPhotoUpload: (detailIndex: number, zoneIndex: number, file: File) => void;
  onPhotoRemove: (detailIndex: number, zoneIndex: number) => void;
  nameError?: string;
  plantTypeError?: string;
  plantNameError?: string;
  soilTypeError?: string;
};

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];
const ACCEPTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.webp'];
const MAX_PHOTO_SIZE = 10 * 1024 * 1024;
const PHOTO_ERROR_MESSAGE = 'JPG, JPEG, PNG, HEIC and WEBP only. Max file size 10MB';

const ZoneDetails: FC<PropTypes> = ({
  zone,
  hasSensor,
  zoneIndex,
  detailIndex,
  onAddZoneSensor,
  onRemoveZone,
  onFieldChange,
  onPhotoUpload,
  onPhotoRemove,
  nameError,
  plantTypeError,
  plantNameError,
  soilTypeError,
}) => {
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoError, setPhotoError] = useState<string | undefined>(undefined);

  // Plant type (family) state — manage search query with debounce
  const [familyResults, setFamilyResults] = useState<PlantFamily[]>([]);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [familySearchQuery, setFamilySearchQuery] = useState('');
  const debouncedFamilyQuery = useDebounce(familySearchQuery, 300);
  // Scientific family name used for filtering plants (e.g. "Rosaceae")
  const [selectedFamilyScientific, setSelectedFamilyScientific] = useState<string>('');

  // Plant name state — manage search query with debounce
  const [plantResults, setPlantResults] = useState<PlantSearchResult[]>([]);
  const [plantLoading, setPlantLoading] = useState(false);
  const [plantLoadingMore, setPlantLoadingMore] = useState(false);
  const [plantSearchQuery, setPlantSearchQuery] = useState('');
  const debouncedPlantQuery = useDebounce(plantSearchQuery, 300);
  const [plantPage, setPlantPage] = useState(1);
  const [plantHasMore, setPlantHasMore] = useState(false);

  // Load families when search query changes
  useEffect(() => {
    let cancelled = false;
    const loadFamilies = async () => {
      const query = debouncedFamilyQuery?.trim();
      if (query && query.length < 2) return;

      try {
        setFamilyLoading(true);
        const response = await plantsApi.families(query || undefined);
        if (!cancelled) {
          setFamilyResults(response.results);
        }
      } catch {
        if (!cancelled) {
          setFamilyResults([]);
        }
      } finally {
        if (!cancelled) {
          setFamilyLoading(false);
        }
      }
    };
    loadFamilies();
    return () => {
      cancelled = true;
    };
  }, [debouncedFamilyQuery]);

  // Load plants when family or search query changes (resets to page 1)
  useEffect(() => {
    let cancelled = false;
    const loadPlants = async () => {
      const query = debouncedPlantQuery?.trim();
      if (query && query.length < 2) return;

      try {
        setPlantLoading(true);
        setPlantPage(1);
        const response = await plantsApi.list(
          selectedFamilyScientific || undefined,
          query || undefined,
        );
        if (!cancelled) {
          setPlantResults(response.results);
          setPlantHasMore(response.results.length < response.total);
        }
      } catch {
        if (!cancelled) {
          setPlantResults([]);
          setPlantHasMore(false);
        }
      } finally {
        if (!cancelled) {
          setPlantLoading(false);
        }
      }
    };
    loadPlants();
    return () => {
      cancelled = true;
    };
  }, [selectedFamilyScientific, debouncedPlantQuery]);

  // Load next page of plants on scroll
  const handlePlantScrollEnd = useCallback(async () => {
    if (plantLoadingMore || !plantHasMore) return;

    const nextPage = plantPage + 1;
    try {
      setPlantLoadingMore(true);
      const query = debouncedPlantQuery?.trim();
      const response = await plantsApi.list(
        selectedFamilyScientific || undefined,
        query || undefined,
        nextPage,
      );
      setPlantResults((prev) => [...prev, ...response.results]);
      setPlantPage(nextPage);
      setPlantHasMore(prev => prev && response.results.length > 0);
    } catch {
      // stop trying on error
      setPlantHasMore(false);
    } finally {
      setPlantLoadingMore(false);
    }
  }, [plantLoadingMore, plantHasMore, plantPage, debouncedPlantQuery, selectedFamilyScientific]);

  const plantTypeOptions = useMemo(() => {
    const options = familyResults.map((f) => ({
      value: f.label,
      label: f.label,
    }));

    return options;
  }, [familyResults]);

  const plantNameOptions = useMemo(() => {
    const options = plantResults.map((plant) => ({
      value: plant.value,
      label: plant.label,
      family: plant.family,
    }));

    return options;
  }, [plantResults]);

  const handleFamilySearchChange = useCallback((query: string) => {
    setFamilySearchQuery(query);
  }, []);

  const handlePlantSearchChange = useCallback((query: string) => {
    setPlantSearchQuery(query);
  }, []);

  const handlePlantTypeChange = (value: string) => {
    // Find the scientific name for API filtering
    const family = familyResults.find((f) => f.label === value);
    setSelectedFamilyScientific(family?.scientificName || value);

    // Clear plant name first, then set new plant type
    // Clearing first ensures Select component detects the value transition
    if (zone?.plantName) {
      onFieldChange(detailIndex, zoneIndex, 'plantName', '');
    }
    onFieldChange(detailIndex, zoneIndex, 'plantType', value);

    setPlantSearchQuery('');
    setPlantResults([]);
    setPlantPage(1);
    setPlantHasMore(false);
  };

  const handlePlantNameChange = async (value: string) => {
    // Set plant name immediately so the UI updates without delay
    onFieldChange(detailIndex, zoneIndex, 'plantName', value);

    const selectedPlant = plantResults.find((p) => p.value === value);
    if (!selectedPlant?.family) return;

    // Look up the family in loaded results to get the display label
    // that matches what plantTypeOptions uses as value
    let matchingFamily = familyResults.find(
      (f) => f.scientificName === selectedPlant.family,
    );

    // If the family isn't in current results (e.g. user searched for a
    // different family earlier), fetch families to find the label
    if (!matchingFamily) {
      try {
        const response = await plantsApi.families(selectedPlant.family);
        if (response.results.length > 0) {
          setFamilyResults(response.results);
          matchingFamily = response.results.find(
            (f) => f.scientificName === selectedPlant.family,
          );
        }
      } catch {
        // Fall through to fallback label
      }
    }

    const familyLabel =
      matchingFamily?.label || selectedPlant.familyCommonName || selectedPlant.family;

    setSelectedFamilyScientific(selectedPlant.family);
    onFieldChange(detailIndex, zoneIndex, 'plantType', familyLabel);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      const isValidType =
        ACCEPTED_IMAGE_TYPES.includes(file.type) ||
        ACCEPTED_IMAGE_EXTENSIONS.includes(fileExtension);
      const isValidSize = file.size <= MAX_PHOTO_SIZE;

      if (isValidType && isValidSize) {
        setPhotoError(undefined);
        onPhotoUpload(detailIndex, zoneIndex, file);
      } else {
        setPhotoError(PHOTO_ERROR_MESSAGE);
      }
    }
    e.target.value = '';
  };

  const hasPhoto = !!(zone?.photo || zone?.photoUrl || zone?.photoOriginalFilename);
  const photoPreviewUrl = useMemo(() => {
    if (zone?.photo) {
      return URL.createObjectURL(zone.photo);
    }

    if (zone?.photoUrl) {
      return zone.photoUrl;
    }

    return undefined;
  }, [zone?.photo, zone?.photoUrl]);

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) {
        URL.revokeObjectURL(photoPreviewUrl);
      }
    };
  }, [photoPreviewUrl]);

  return (
    <div className={classNames('step-two__zone', 'has-parent')}>
      <Card
        title="Zone"
        className={classNames({
          'has-child': hasSensor,
        })}
        onRemove={!!onRemoveZone ? () => onRemoveZone(detailIndex, zoneIndex) : undefined}
      >
        <div className="step-two__zone-fields">
          <Input
            value={zone?.name}
            label={'Zone name'}
            placeholder="Name"
            wrapperClassName="step-two__zone-field"
            name={`zone-name-${detailIndex}-${zoneIndex}`}
            onChange={(e) => onFieldChange(detailIndex, zoneIndex, 'name', e.target.value)}
            maxLength={100}
            error={nameError}
            variant="white"
          />

          <Select
            withSearch
            label="Plant type"
            value={zone?.plantType}
            placeholder="Select plant type..."
            name={`zone-plant-type-${detailIndex}-${zoneIndex}`}
            options={plantTypeOptions}
            className="step-two__zone-field"
            onChange={handlePlantTypeChange}
            onSearchChange={handleFamilySearchChange}
            loading={familyLoading}
            error={plantTypeError}
          />

          <Select
            withSearch
            label="Plant name"
            value={zone?.plantName}
            placeholder="Select plant name..."
            name={`zone-plant-name-${detailIndex}-${zoneIndex}`}
            options={plantNameOptions}
            className="step-two__zone-field"
            onChange={handlePlantNameChange}
            onSearchChange={handlePlantSearchChange}
            onScrollEnd={handlePlantScrollEnd}
            loading={plantLoading}
            loadingMore={plantLoadingMore}
            error={plantNameError}
          />

          <Select
            withSearch
            label="Soil type"
            value={zone?.soilType}
            placeholder="Soil type"
            name={`zone-soil-type-${detailIndex}-${zoneIndex}`}
            options={SOIL_TYPE}
            className="step-two__zone-field"
            onChange={(value: string) => onFieldChange(detailIndex, zoneIndex, 'soilType', value)}
            error={soilTypeError}
          />
        </div>

        <div
          className={classNames('step-two__zone-photo', {
            'step-two__zone-photo--has-file': hasPhoto,
          })}
          onClick={() => !hasPhoto && photoInputRef.current?.click()}
        >
          {hasPhoto ? (
            <div className="step-two__zone-photo-preview">
              {photoPreviewUrl ? (
                <img
                  src={photoPreviewUrl}
                  alt="Zone photo"
                  className="step-two__zone-photo-image"
                />
              ) : (
                <span className="step-two__zone-photo-filename">{zone?.photoOriginalFilename}</span>
              )}
              <button
                type="button"
                className="step-two__zone-photo-remove"
                onClick={(e) => {
                  e.stopPropagation();
                  onPhotoRemove(detailIndex, zoneIndex);
                }}
              >
                <TrashIcon />
              </button>
            </div>
          ) : (
            <>
              <div className="step-two__zone-photo-icon">
                <PlusIcon />
              </div>
              <span className="step-two__zone-photo-title">Upload photo</span>
              <span className="step-two__zone-photo-hint">
                JPG, JPEG, PNG, HEIC, WEBP up to 10MB
              </span>
            </>
          )}
          <input
            ref={photoInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.heic,.webp"
            onChange={handlePhotoChange}
            className="step-two__zone-photo-input"
          />
          {photoError && <span className="step-two__zone-photo-error">{photoError}</span>}
        </div>

        <Button
          type={'button'}
          variant="secondary"
          onClick={() => onAddZoneSensor(detailIndex, zoneIndex)}
        >
          Add new zone sensor
        </Button>
      </Card>
    </div>
  );
};

export default memo(ZoneDetails);
